/**
 * `LandingPageData` ↔ JSON serialization.
 *
 * The flat-Map data model uses `Map` for instances, props, styles,
 * styleSources, styleSourceSelections, and breakpoints. `JSON.stringify`
 * doesn't serialize Map natively (it produces `{}`), so we explicitly
 * convert to/from a plain-object form on every persistence boundary.
 *
 * The JSON shape is the wire format — this is what the server receives
 * and what gets persisted to `LandingPage.contentJson` for `format:
 * 'tree-v1'` pages. Keeping the shape stable matters because:
 *   1. The version-rebase protocol depends on byte-equal round-trips
 *      (a save + reload should produce identical state).
 *   2. Once persisted, customer pages live with this shape forever.
 *      Format changes need an explicit migrator (cf. `format` discriminator).
 *
 * Validation on parse is defensive: malformed input (wrong types, missing
 * required fields) returns a `ParseError` rather than throwing. The caller
 * decides whether to fail open or surface the error to the user.
 */

import type {
  Breakpoint,
  BreakpointId,
  Instance,
  InstanceType,
  LandingPageData,
  NodeId,
  NodeProps,
  PseudoState,
  SourceId,
  StyleDecl,
  StyleKey,
  StyleSource,
  StyleSourceSelection,
} from './types';
import { styleKey } from './types';

// ─────────────────────────────────────────────────────────────────────────
// Wire format
// ─────────────────────────────────────────────────────────────────────────

/**
 * Persisted shape of a single landing page tree. Versioned by the
 * top-level `format` discriminator on `LandingPage.contentJson`; this
 * is `format: 'tree-v1'`.
 *
 * Every Map in `LandingPageData` lands here as an array of entries.
 * Arrays serialize natively in JSON and round-trip cleanly through
 * `JSON.stringify`.
 */
export interface SerializedLandingPageData {
  format: 'tree-v1';
  rootId: NodeId;
  version: number;
  instances: Array<Instance>;
  /** Per-instance props as `[instanceId, NodeProps]` pairs. */
  props: Array<[NodeId, NodeProps]>;
  /** All style declarations in entry order. The `StyleKey` is rebuilt
   *  on parse from the decl's fields rather than persisted as a string,
   *  because the key shape is implementation detail. */
  styles: Array<StyleDecl>;
  /** Style source registry as a flat list. */
  styleSources: Array<StyleSource>;
  /** Per-instance source selections as `[instanceId, selection]` pairs. */
  styleSourceSelections: Array<[NodeId, StyleSourceSelection]>;
  /** Breakpoints as a flat list. The `id` is the natural key. */
  breakpoints: Array<Breakpoint>;
}

// ─────────────────────────────────────────────────────────────────────────
// Serialize
// ─────────────────────────────────────────────────────────────────────────

/**
 * Convert in-memory `LandingPageData` to the `SerializedLandingPageData`
 * wire format. Pure — no I/O. Map iteration order is preserved (V8 +
 * modern JS engines guarantee insertion order on Map.iterator), so
 * a save/reload round-trip produces identical state.
 */
export function serializeLandingPageData(data: LandingPageData): SerializedLandingPageData {
  return {
    format: 'tree-v1',
    rootId: data.rootId,
    version: data.version,
    instances: Array.from(data.instances.values()),
    props: Array.from(data.props.entries()),
    styles: Array.from(data.styles.values()),
    styleSources: Array.from(data.styleSources.values()),
    styleSourceSelections: Array.from(data.styleSourceSelections.entries()),
    breakpoints: Array.from(data.breakpoints.values()),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Parse
// ─────────────────────────────────────────────────────────────────────────

export interface ParseSuccess {
  ok: true;
  data: LandingPageData;
}

export interface ParseError {
  ok: false;
  /** Stable error code for programmatic handling. */
  code:
    | 'invalid-shape'
    | 'unsupported-format'
    | 'missing-required-field'
    | 'malformed-instance'
    | 'malformed-style';
  message: string;
  /** Path into the input where the error was found, for debugging. */
  path?: string;
}

export type ParseResult = ParseSuccess | ParseError;

const err = (code: ParseError['code'], message: string, path?: string): ParseError => ({
  ok: false,
  code,
  message,
  path,
});

/**
 * Parse a wire-format JSON object into `LandingPageData`. Defensive:
 * returns `ParseError` rather than throwing on malformed input.
 *
 * The caller is responsible for trust — if `input` came from the
 * server, validation is a defense-in-depth check, not the primary
 * gatekeeper. If it came from user-pasted JSON or third-party
 * tooling, validation is load-bearing.
 */
export function parseLandingPageData(input: unknown): ParseResult {
  if (!input || typeof input !== 'object') {
    return err('invalid-shape', 'Expected an object at the top level');
  }
  const obj = input as Record<string, unknown>;

  // Format discriminator. We only know how to parse 'tree-v1'.
  if (obj.format !== 'tree-v1') {
    return err(
      'unsupported-format',
      `Unsupported format: ${String(obj.format)}. Expected 'tree-v1'.`,
      'format',
    );
  }

  // Required scalars.
  if (typeof obj.rootId !== 'string' || obj.rootId.length === 0) {
    return err('missing-required-field', 'rootId must be a non-empty string', 'rootId');
  }
  if (typeof obj.version !== 'number' || !Number.isFinite(obj.version)) {
    return err('missing-required-field', 'version must be a finite number', 'version');
  }

  // Instances.
  if (!Array.isArray(obj.instances)) {
    return err('missing-required-field', 'instances must be an array', 'instances');
  }
  const instances = new Map<NodeId, Instance>();
  for (let i = 0; i < obj.instances.length; i++) {
    const raw = obj.instances[i] as Record<string, unknown> | null;
    if (!raw || typeof raw !== 'object') {
      return err('malformed-instance', `instances[${i}] is not an object`, `instances[${i}]`);
    }
    if (typeof raw.id !== 'string' || raw.id.length === 0) {
      return err('malformed-instance', `instances[${i}].id missing or invalid`, `instances[${i}].id`);
    }
    if (typeof raw.type !== 'string') {
      return err('malformed-instance', `instances[${i}].type missing`, `instances[${i}].type`);
    }
    if (!Array.isArray(raw.children)) {
      return err('malformed-instance', `instances[${i}].children must be an array`, `instances[${i}].children`);
    }
    instances.set(raw.id, {
      id: raw.id,
      type: raw.type as InstanceType,
      children: raw.children.filter((c): c is string => typeof c === 'string'),
      label: typeof raw.label === 'string' ? raw.label : undefined,
    });
  }

  // Props (entries).
  const props = new Map<NodeId, NodeProps>();
  if (Array.isArray(obj.props)) {
    for (let i = 0; i < obj.props.length; i++) {
      const entry = obj.props[i] as unknown[];
      if (!Array.isArray(entry) || entry.length !== 2) {
        return err('malformed-instance', `props[${i}] must be a [id, props] pair`, `props[${i}]`);
      }
      const [id, p] = entry;
      if (typeof id !== 'string') {
        return err('malformed-instance', `props[${i}][0] must be a string id`, `props[${i}][0]`);
      }
      if (!p || typeof p !== 'object') {
        return err('malformed-instance', `props[${i}][1] must be an object`, `props[${i}][1]`);
      }
      props.set(id, p as NodeProps);
    }
  }

  // Styles.
  const styles = new Map<StyleKey, StyleDecl>();
  if (Array.isArray(obj.styles)) {
    for (let i = 0; i < obj.styles.length; i++) {
      const raw = obj.styles[i] as Record<string, unknown> | null;
      if (!raw || typeof raw !== 'object') {
        return err('malformed-style', `styles[${i}] is not an object`, `styles[${i}]`);
      }
      const sourceId = raw.sourceId;
      const breakpointId = raw.breakpointId;
      const property = raw.property;
      const state = raw.state;
      const value = raw.value;
      if (
        typeof sourceId !== 'string' ||
        typeof breakpointId !== 'string' ||
        typeof property !== 'string' ||
        typeof state !== 'string' ||
        !value || typeof value !== 'object'
      ) {
        return err('malformed-style', `styles[${i}] has missing/invalid fields`, `styles[${i}]`);
      }
      const v = value as { type?: unknown; tokenId?: unknown; value?: unknown };
      const validValue =
        (v.type === 'token' && typeof v.tokenId === 'string') ||
        (v.type === 'literal' && typeof v.value === 'string');
      if (!validValue) {
        return err('malformed-style', `styles[${i}].value malformed`, `styles[${i}].value`);
      }
      const decl: StyleDecl = {
        sourceId: sourceId as SourceId,
        breakpointId: breakpointId as BreakpointId,
        property,
        state: state as PseudoState,
        value: v as StyleDecl['value'],
      };
      styles.set(styleKey(decl.sourceId, decl.breakpointId, decl.property, decl.state), decl);
    }
  }

  // Style sources.
  const styleSources = new Map<SourceId, StyleSource>();
  if (Array.isArray(obj.styleSources)) {
    for (let i = 0; i < obj.styleSources.length; i++) {
      const raw = obj.styleSources[i] as Record<string, unknown> | null;
      if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string' || typeof raw.type !== 'string') {
        return err('malformed-style', `styleSources[${i}] missing/invalid`, `styleSources[${i}]`);
      }
      if (raw.type === 'local') {
        if (typeof raw.instanceId !== 'string') {
          return err('malformed-style', `styleSources[${i}] local source missing instanceId`, `styleSources[${i}]`);
        }
        styleSources.set(raw.id, { id: raw.id, type: 'local', instanceId: raw.instanceId });
      } else if (raw.type === 'token') {
        if (typeof raw.name !== 'string') {
          return err('malformed-style', `styleSources[${i}] token source missing name`, `styleSources[${i}]`);
        }
        styleSources.set(raw.id, { id: raw.id, type: 'token', name: raw.name });
      } else {
        return err('malformed-style', `styleSources[${i}].type unknown`, `styleSources[${i}].type`);
      }
    }
  }

  // Style source selections.
  const styleSourceSelections = new Map<NodeId, StyleSourceSelection>();
  if (Array.isArray(obj.styleSourceSelections)) {
    for (let i = 0; i < obj.styleSourceSelections.length; i++) {
      const entry = obj.styleSourceSelections[i] as unknown[];
      if (!Array.isArray(entry) || entry.length !== 2) {
        return err(
          'malformed-style',
          `styleSourceSelections[${i}] must be a [id, selection] pair`,
          `styleSourceSelections[${i}]`,
        );
      }
      const [id, sel] = entry;
      if (typeof id !== 'string' || !sel || typeof sel !== 'object') {
        return err(
          'malformed-style',
          `styleSourceSelections[${i}] malformed`,
          `styleSourceSelections[${i}]`,
        );
      }
      const s = sel as { instanceId?: unknown; values?: unknown };
      if (typeof s.instanceId !== 'string' || !Array.isArray(s.values)) {
        return err(
          'malformed-style',
          `styleSourceSelections[${i}] missing instanceId/values`,
          `styleSourceSelections[${i}]`,
        );
      }
      styleSourceSelections.set(id, {
        instanceId: s.instanceId,
        values: s.values.filter((v): v is string => typeof v === 'string'),
      });
    }
  }

  // Breakpoints.
  const breakpoints = new Map<BreakpointId, Breakpoint>();
  if (Array.isArray(obj.breakpoints)) {
    for (let i = 0; i < obj.breakpoints.length; i++) {
      const raw = obj.breakpoints[i] as Record<string, unknown> | null;
      if (
        !raw ||
        typeof raw !== 'object' ||
        typeof raw.id !== 'string' ||
        typeof raw.label !== 'string' ||
        typeof raw.minWidth !== 'number'
      ) {
        return err(
          'malformed-instance',
          `breakpoints[${i}] missing required fields`,
          `breakpoints[${i}]`,
        );
      }
      breakpoints.set(raw.id, {
        id: raw.id,
        label: raw.label,
        minWidth: raw.minWidth,
      });
    }
  }

  // Final shape validation: rootId must reference a known instance.
  if (!instances.has(obj.rootId as string)) {
    return err(
      'invalid-shape',
      `rootId ${String(obj.rootId)} not present in instances`,
      'rootId',
    );
  }

  return {
    ok: true,
    data: {
      instances,
      props,
      styles,
      styleSources,
      styleSourceSelections,
      breakpoints,
      rootId: obj.rootId as NodeId,
      version: obj.version,
    },
  };
}
